import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import './PaymentStripe.css';

function PaymentStripe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const clientSecret = searchParams.get('client_secret');
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [error, setError] = useState(null);

  const verifyPayment = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await apiClient.post(
        '/payment/verify',
        {
          order_id: orderId,
          payment_method: 'stripe',
          session_id: clientSecret
        },
        { headers }
      );

      // Nukreipti į užsakymo puslapį
      setTimeout(() => {
        navigate(`/orders/${orderId}`);
      }, 2000);
    } catch (error) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, clientSecret]);

  const initializeStripe = useCallback(() => {
    try {
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
      
      if (!stripe) {
        setError('Stripe nėra sukonfigūruotas');
        return;
      }

      // Sukurti Payment Element
      const elements = stripe.elements({
        clientSecret: clientSecret,
        appearance: {
          theme: 'stripe',
        },
      });

      const paymentElement = elements.create('payment');
      paymentElement.mount('#payment-element');

      // Apdoroti formos pateikimą
      const form = document.getElementById('payment-form');
      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          setPaymentStatus('processing');

          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message);
            setPaymentStatus('error');
            return;
          }

          // Patvirtinti mokėjimą
          const { error: confirmError } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: `${window.location.origin}/payment/stripe/return?order_id=${orderId}`,
            },
            redirect: 'if_required',
          });

          if (confirmError) {
            setError(confirmError.message);
            setPaymentStatus('error');
          } else {
            // Patikrinti mokėjimo statusą
            const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
            
            if (paymentIntent.status === 'succeeded') {
              setPaymentStatus('success');
              // Patikrinti mokėjimo statusą per API
              setTimeout(() => {
                verifyPayment();
              }, 1000);
            } else {
              setPaymentStatus('error');
              setError('Mokėjimas nepavyko');
            }
          }
        });
      }
    } catch (err) {
      setError(err.message);
      setPaymentStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, clientSecret, verifyPayment]);

  useEffect(() => {
    if (!orderId || !clientSecret) {
      setError('Trūksta mokėjimo duomenų');
      return;
    }

    // Load Stripe.js
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      initializeStripe();
    };
    script.onerror = () => {
      setError('Nepavyko užkrauti Stripe bibliotekos');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [orderId, clientSecret, initializeStripe]);

  if (error && !clientSecret) {
    return (
      <div className="payment-stripe-page container">
        <div className="payment-error">
          <h2>Klaida</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/checkout')} className="btn btn-primary">
            Grįžti į checkout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-stripe-page container">
      <div className="payment-container">
        <h1>Mokėjimas</h1>
        
        {paymentStatus === 'success' ? (
          <div className="payment-success">
            <h2>✅ Mokėjimas sėkmingas!</h2>
            <p>Jūsų užsakymas yra apdorojamas. Netrukus būsite nukreipti...</p>
          </div>
        ) : paymentStatus === 'error' ? (
          <div className="payment-error">
            <h2>❌ Mokėjimas nepavyko</h2>
            <p>{error || 'Įvyko klaida apdorojant mokėjimą'}</p>
            <button onClick={() => navigate(`/orders/${orderId}`)} className="btn btn-primary">
              Peržiūrėti užsakymą
            </button>
          </div>
        ) : (
          <>
            <p>Užpildykite mokėjimo informaciją:</p>
            <form id="payment-form">
              <div id="payment-element"></div>
              <button type="submit" className="btn btn-primary btn-large" id="submit-button">
                Mokėti
              </button>
            </form>
            <div id="payment-message" className="hidden"></div>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentStripe;
